import React from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  GridItem,
  VStack,
  HStack,
  SimpleGrid,
  Progress,
  Badge,
  useColorModeValue,
  Icon,
  Divider,
  Select,
  Button
} from '@chakra-ui/react';
import { 
  BarChart2, 
  PieChart, 
  TrendingUp, 
  Calendar, 
  Filter,
  Brain,
  Zap,
  MessageSquare,
  Users,
  Tag as TagIcon,
  Clock,
  Download
} from 'lucide-react';

const AnalyticsPanel: React.FC = () => {
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const methodistBlue = '#0066CC';
  
  // Sample sentiment data
  const sentimentData = [
    { label: 'Positive', value: 62, color: 'green.400' },
    { label: 'Neutral', value: 24, color: 'blue.400' },
    { label: 'Negative', value: 14, color: 'red.400' }
  ];
  
  // Sample topic clusters
  const topicClusters = [
    { name: 'UI/UX Improvements', count: 87, sentiment: 'positive' },
    { name: 'Stress Management Tools', count: 64, sentiment: 'positive' },
    { name: 'Mobile Accessibility', count: 52, sentiment: 'neutral' },
    { name: 'EHR Integration', count: 49, sentiment: 'negative' },
    { name: 'Peer Support Features', count: 43, sentiment: 'positive' },
    { name: 'Notification System', count: 38, sentiment: 'negative' },
  ];
  
  // Sample department engagement
  const departmentEngagement = [
    { name: 'Emergency Medicine', engagement: 78, ideas: 42, contributors: 18 },
    { name: 'Internal Medicine', engagement: 65, ideas: 38, contributors: 15 },
    { name: 'Psychiatry', engagement: 82, ideas: 35, contributors: 12 },
    { name: 'Surgery', engagement: 45, ideas: 29, contributors: 10 },
    { name: 'Pediatrics', engagement: 58, ideas: 27, contributors: 9 },
    { name: 'Administration', engagement: 72, ideas: 25, contributors: 8 },
  ];
  
  // Sample implementation metrics
  const implementationMetrics = [
    { label: 'Implemented', value: 32, color: 'green.400' },
    { label: 'In Progress', value: 18, color: 'orange.400' },
    { label: 'Planned', value: 27, color: 'blue.400' },
    { label: 'Not Feasible', value: 23, color: 'red.400' }
  ];
  
  // Sample LLM insights
  const llmInsights = [
    {
      title: 'Emerging Trend: Personalization',
      description: 'There is a significant increase in requests for personalized experiences across multiple platform areas.',
      confidence: 92,
      impact: 'High',
      relatedIdeas: 37
    },
    {
      title: 'Implementation Opportunity: Quick Wellness Checks',
      description: 'Multiple similar ideas around quick wellness assessments could be consolidated into a single feature.',
      confidence: 88,
      impact: 'Medium',
      relatedIdeas: 24
    },
    {
      title: 'User Pain Point: Notification Overload',
      description: 'Analysis of feedback reveals frustration with the frequency and timing of system notifications.',
      confidence: 95,
      impact: 'High',
      relatedIdeas: 18
    },
    {
      title: 'Cross-Pillar Connection: Stress and Efficiency',
      description: 'Strong correlation detected between stress management tools and practice efficiency features.',
      confidence: 84,
      impact: 'Medium',
      relatedIdeas: 29
    },
  ];
  
  return (
    <Grid templateColumns="1fr 300px" gap={4}>
      {/* Main content */}
      <GridItem>
        <VStack spacing={4} align="stretch">
          {/* Filters */}
          <Flex justify="space-between" align="center">
            <Heading size="sm">LLM-Powered Analytics</Heading>
            <HStack spacing={3}>
              <Select size="sm" maxW="150px" defaultValue="30days">
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </Select>
              <Button size="sm" leftIcon={<Download size={14} />} variant="outline">Export</Button>
            </HStack>
          </Flex>
          
          {/* LLM Insights */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Flex align="center" mb={3}>
              <Icon as={Brain} color="purple.500" mr={2} />
              <Heading size="sm">AI-Generated Insights</Heading>
            </Flex>
            <VStack spacing={3} align="stretch">
              {llmInsights.map((insight, index) => (
                <Box 
                  key={index} 
                  p={3} 
                  borderRadius="md" 
                  bg={useColorModeValue('gray.50', 'gray.700')}
                  borderLeft="4px solid"
                  borderLeftColor={insight.impact === 'High' ? 'red.400' : 'orange.400'}
                >
                  <Flex justify="space-between" align="center" mb={1}>
                    <HStack>
                      <Heading size="xs">{insight.title}</Heading>
                      <Badge colorScheme={insight.impact === 'High' ? 'red' : 'orange'}>{insight.impact} Impact</Badge>
                    </HStack>
                    <Badge colorScheme="purple">{insight.confidence}% Confidence</Badge>
                  </Flex>
                  <Text fontSize="sm" mb={2}>{insight.description}</Text>
                  <Flex justify="flex-end">
                    <Text fontSize="xs" color="blue.500">{insight.relatedIdeas} related ideas</Text>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
          
          {/* Sentiment Analysis */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Flex align="center" mb={3}>
              <Icon as={MessageSquare} color="blue.500" mr={2} />
              <Heading size="sm">Sentiment Analysis</Heading>
            </Flex>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Heading size="xs" mb={3}>Overall Sentiment</Heading>
                <VStack spacing={3} align="stretch">
                  {sentimentData.map((item, index) => (
                    <Box key={index}>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">{item.label}</Text>
                        <Text fontSize="sm" fontWeight="bold">{item.value}%</Text>
                      </Flex>
                      <Progress value={item.value} size="sm" colorScheme={item.color.split('.')[0]} borderRadius="full" />
                    </Box>
                  ))}
                </VStack>
              </Box>
              
              <Box>
                <Heading size="xs" mb={3}>Topic Clusters</Heading>
                <VStack spacing={2} align="stretch">
                  {topicClusters.map((topic, index) => (
                    <Flex key={index} justify="space-between" align="center">
                      <HStack>
                        <Badge 
                          colorScheme={topic.sentiment === 'positive' ? 'green' : 
                                     topic.sentiment === 'negative' ? 'red' : 'blue'}
                          variant="solid"
                          borderRadius="full"
                          w="10px"
                          h="10px"
                          p={0}
                        />
                        <Text fontSize="sm" noOfLines={1}>{topic.name}</Text>
                      </HStack>
                      <Text fontSize="sm" fontWeight="medium">{topic.count}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>
          
          {/* Department Engagement */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Flex align="center" mb={3}>
              <Icon as={Users} color="green.500" mr={2} />
              <Heading size="sm">Department Engagement</Heading>
            </Flex>
            <VStack spacing={3} align="stretch">
              {departmentEngagement.map((dept, index) => (
                <Box key={index}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm">{dept.name}</Text>
                    <HStack spacing={3}>
                      <Badge colorScheme="blue" variant="outline">{dept.ideas} ideas</Badge>
                      <Badge colorScheme="green" variant="outline">{dept.contributors} contributors</Badge>
                      <Text fontSize="sm" fontWeight="bold">{dept.engagement}%</Text>
                    </HStack>
                  </Flex>
                  <Progress 
                    value={dept.engagement} 
                    size="sm" 
                    colorScheme={dept.engagement > 70 ? 'green' : dept.engagement > 50 ? 'blue' : 'orange'} 
                    borderRadius="full" 
                  />
                </Box>
              ))}
            </VStack>
          </Box>
        </VStack>
      </GridItem>
      
      {/* Sidebar */}
      <GridItem>
        <VStack spacing={4} align="stretch">
          {/* Implementation Metrics */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>Implementation Metrics</Heading>
            <VStack spacing={3} align="stretch">
              {implementationMetrics.map((metric, index) => (
                <Box key={index}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm">{metric.label}</Text>
                    <Text fontSize="sm" fontWeight="bold">{metric.value}%</Text>
                  </Flex>
                  <Progress value={metric.value} size="sm" colorScheme={metric.color.split('.')[0]} borderRadius="full" />
                </Box>
              ))}
            </VStack>
          </Box>
          
          {/* Key Metrics */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>LLM Processing Metrics</Heading>
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between">
                <Text fontSize="sm">Avg. Processing Time</Text>
                <Text fontSize="sm" fontWeight="bold">1.2s</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="sm">Classification Accuracy</Text>
                <Text fontSize="sm" fontWeight="bold">94.3%</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="sm">Similarity Detection</Text>
                <Text fontSize="sm" fontWeight="bold">91.7%</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="sm">Sentiment Accuracy</Text>
                <Text fontSize="sm" fontWeight="bold">89.5%</Text>
              </Flex>
              <Flex justify="space-between">
                <Text fontSize="sm">Daily Processed Ideas</Text>
                <Text fontSize="sm" fontWeight="bold">~42</Text>
              </Flex>
            </VStack>
          </Box>
          
          {/* Model Training */}
          <Box 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>Model Training Status</Heading>
            <VStack spacing={3} align="stretch">
              <HStack>
                <Icon as={Brain} color="purple.500" />
                <Text fontSize="sm" fontWeight="medium">Last Training: 3 days ago</Text>
              </HStack>
              <Box>
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="xs">Model Performance</Text>
                  <Text fontSize="xs" fontWeight="bold">92.8%</Text>
                </Flex>
                <Progress value={92.8} size="sm" colorScheme="purple" borderRadius="full" />
              </Box>
              <HStack>
                <Icon as={Zap} color="orange.500" />
                <Text fontSize="sm">Training on 1,243 new data points</Text>
              </HStack>
              <Button size="sm" leftIcon={<Brain size={14} />} colorScheme="purple" variant="outline">
                View Model Details
              </Button>
            </VStack>
          </Box>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default AnalyticsPanel;
